/**
 * WalletSaveCard — how a minted pass gets onto a phone, per platform it was
 * minted for: a QR to scan with that phone (the demo usually runs on a
 * desktop) and the add/save button (the path on the phone itself). Shared by
 * the studios' success panel and the pass page, so both say the same thing.
 * A platform the pass was not minted for is simply absent — the pass page
 * used to show a disabled "Google Wallet (nem elérhető)" on every Apple pass.
 */
import { QRCodeSVG } from 'qrcode.react';
import type { ReactNode } from 'react';

import { useT } from '../i18n';

import { AppleWalletLink } from './AppleWalletLink';

export interface WalletSaveCardProps {
  appleSaveUrl: string | null;
  googleSaveUrl: string | null;
}

function SaveTarget({
  url,
  qrAria,
  title,
  hint,
  tapHint,
  note,
  action,
}: {
  url: string;
  qrAria: string;
  title: string;
  /** Desktop: scan the QR with the phone. */
  hint: string;
  /** Phone: the QR is hidden (it cannot scan itself) — tap the button. */
  tapHint: string;
  note?: ReactNode;
  action: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
      {/* A phone cannot scan its own screen — the QR is for the desktop demo. */}
      <div className="hidden sm:block rounded-lg bg-white p-3 ring-1 ring-slate-200 self-start">
        <QRCodeSVG value={url} size={132} level="M" aria-label={qrAria} />
      </div>
      <div className="space-y-2 text-sm text-slate-700 min-w-0">
        <p className="font-semibold text-slate-900">{title}</p>
        <p className="hidden sm:block">{hint}</p>
        <p className="sm:hidden">{tapHint}</p>
        {note ? <p className="hidden sm:block text-xs text-slate-500">{note}</p> : null}
        <div className="pt-1">{action}</div>
      </div>
    </div>
  );
}

export function WalletSaveCard({ appleSaveUrl, googleSaveUrl }: WalletSaveCardProps): JSX.Element {
  const t = useT();
  if (!appleSaveUrl && !googleSaveUrl) {
    return <p className="text-sm text-slate-500">{t('wallet.none')}</p>;
  }
  return (
    <div className="space-y-6">
      {appleSaveUrl ? (
        <SaveTarget
          url={appleSaveUrl}
          qrAria={t('mint.appleQrAria')}
          title="Apple Wallet"
          hint={t('mint.appleQrHint')}
          tapHint={t('mint.appleTapHint')}
          note={
            <>
              {t('mint.appleDesktopHintPrefix')}
              <code>.pkpass</code>
              {t('mint.appleDesktopHintRest')}
            </>
          }
          action={<AppleWalletLink saveUrl={appleSaveUrl} />}
        />
      ) : null}
      {googleSaveUrl ? (
        <SaveTarget
          url={googleSaveUrl}
          qrAria={t('mint.googleQrAria')}
          title="Google Wallet"
          hint={t('mint.googleQrHint')}
          tapHint={t('mint.googleTapHint')}
          action={
            // An ordinary same-tab link (no forced download, no popup tab) —
            // on the phone it hands straight over to Google Wallet.
            <a href={googleSaveUrl} className="btn-secondary">
              {t('mint.addGoogleWallet')}
            </a>
          }
        />
      ) : null}
    </div>
  );
}
